// src/features/items/itemsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

const initialState = {
  items: [],
  currentItem: null,
  loading: false,
  error: null,
  uploadProgress: 0,
};

// Async Thunks

// Fetch all items for the current user
export const fetchItems = createAsyncThunk(
  'items/fetchItems',
  async (_, { rejectWithValue }) => {
    try {
      const items = await apiService.getMyAssets();
      return items;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch items');
    }
  }
);

// Fetch a single item by ID or slug
export const fetchItemById = createAsyncThunk(
  'items/fetchItemById',
  async (itemId, { rejectWithValue }) => {
    try {
      const item = await apiService.getAsset(itemId);
      return item;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch item');
    }
  }
);

// Upload/Create new item
export const uploadItem = createAsyncThunk(
  'items/uploadItem',
  async (formData, { rejectWithValue, dispatch }) => {
    try {
      // Start upload progress simulation
      dispatch(setUploadProgress(10));

      // Create FormData if not already
      const uploadFormData = new FormData();
      
      // Append file
      if (formData.file) {
        uploadFormData.append('file', formData.file);
      }
      
      // Append thumbnail if exists
      if (formData.thumbnail) {
        uploadFormData.append('thumbnail', formData.thumbnail);
      }
      
      // Append screenshots if exists
      if (formData.screenshots && formData.screenshots.length > 0) {
        formData.screenshots.forEach((screenshot, index) => {
          uploadFormData.append('screenshots', screenshot);
        });
      }
      console.log(uploadFormData.screenshots)
      console.log(formData.screenshots)
      // Append other fields
      uploadFormData.append('title', formData.title);
      uploadFormData.append('description', formData.description || '');
      uploadFormData.append('price', formData.price);
      uploadFormData.append('category', formData.category);
      uploadFormData.append('file_type', formData.fileType);
      uploadFormData.append('license', JSON.stringify(formData.license || []));

      dispatch(setUploadProgress(30));
      console.log('uploadFormData.append',uploadFormData)
      // Call API
      const result = await apiService.createAsset(uploadFormData, (progress) => {
        // Update progress if API supports it
        dispatch(setUploadProgress(30 + (progress * 0.7))); // 30% to 100%
      });

      dispatch(setUploadProgress(100));

      // Format the response to match our state structure
      const newItem = {
        id: result.asset_id || result.id,
        asset_id: result.asset_id,
        slug: result.slug,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        fileType: formData.fileType,
        price: formData.price,
        license: formData.license || [],
        link: result.mintlink || result.link,
        thumbnail: result.thumbnail_url || './thumbnail.png',
        coverImage: result.thumbnail_url || result.cover_image || './thumbnail.png',
        screenshots: result.screenshots || [],
        fileSize: formData.file ? `${(formData.file.size / (1024 * 1024)).toFixed(1)} MB` : '0 MB',
        downloads: 0,
        earnings: 0,
        createdAt: result.created_at || new Date().toISOString(),
        updatedAt: result.updated_at || new Date().toISOString(),
      };

      setTimeout(() => dispatch(setUploadProgress(0)), 1000);

      return newItem;
    } catch (error) {
      dispatch(setUploadProgress(0));
      return rejectWithValue(error.message || 'Failed to upload item');
    }
  }
);

// Update an existing item
export const updateItemDetails = createAsyncThunk(
  'items/updateItem',
  async ({ itemId, updates }, { rejectWithValue }) => {
    try {
      const updatedItem = await apiService.updateAsset(itemId, updates);
      return updatedItem;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update item');
    }
  }
);

// Delete an item
export const deleteItemById = createAsyncThunk(
  'items/deleteItem',
  async (itemId, { rejectWithValue }) => {
    try {
      await apiService.deleteAsset(itemId);
      return itemId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete item');
    }
  }
);

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    setItems: (state, action) => {
      state.items = action.payload;
    },
    setCurrentItem: (state, action) => {
      state.currentItem = action.payload;
    },
    addItem: (state, action) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action) => {
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    deleteItem: (state, action) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Items
      .addCase(fetchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Item By ID
      .addCase(fetchItemById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
      })
      .addCase(fetchItemById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Upload Item
      .addCase(uploadItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.currentItem = action.payload;
      })
      .addCase(uploadItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Item
      .addCase(updateItemDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateItemDetails.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.payload };
        }
        if (state.currentItem?.id === action.payload.id) {
          state.currentItem = { ...state.currentItem, ...action.payload };
        }
      })
      .addCase(updateItemDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Item
      .addCase(deleteItemById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteItemById.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        if (state.currentItem?.id === action.payload) {
          state.currentItem = null;
        }
      })
      .addCase(deleteItemById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setItems,
  setCurrentItem,
  addItem,
  updateItem,
  deleteItem,
  setLoading,
  setError,
  setUploadProgress,
  clearError,
} = itemsSlice.actions;

export default itemsSlice.reducer;